import { useEffect, useState, useRef } from "react";

/**
 * Screenshot deterrent for store-tier users.
 *
 * Web browsers do NOT let JS truly block OS-level screenshots, but we can:
 *   1. Black the screen INSTANTLY on screenshot shortcut keys, window blur,
 *      visibilitychange — so any captured image is just a black rectangle.
 *   2. Reject `getDisplayMedia` (screen recording) at the API level.
 *   3. Apply a near-invisible diagonal watermark with store code + timestamp
 *      as a forensic last-resort (visible only on close inspection of a leak).
 *   4. Disable copy / right-click / Ctrl-S / Ctrl-P.
 *
 * Active ONLY when `enabled === true` (store-tier users).
 */
export default function ScreenshotGuard({ enabled, storeCode }) {
  const [blackout, setBlackout] = useState(false);
  const releaseTimer = useRef(null);

  // 1. Disable selection/copy/contextmenu + intercept getDisplayMedia
  useEffect(() => {
    if (!enabled) return undefined;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    document.body.style.webkitUserSelect = "none";

    const onCopy = (e) => { e.preventDefault(); };
    const onContextMenu = (e) => { e.preventDefault(); };
    document.addEventListener("copy", onCopy);
    document.addEventListener("contextmenu", onContextMenu);

    const md = navigator.mediaDevices;
    let origGetDisplay = null;
    if (md && md.getDisplayMedia) {
      origGetDisplay = md.getDisplayMedia.bind(md);
      md.getDisplayMedia = () =>
        Promise.reject(new DOMException(
          "Screen capture is restricted for store-level accounts.",
          "NotAllowedError"
        ));
    }

    return () => {
      document.body.style.userSelect = prevUserSelect;
      document.body.style.webkitUserSelect = "";
      document.removeEventListener("copy", onCopy);
      document.removeEventListener("contextmenu", onContextMenu);
      if (origGetDisplay && md) md.getDisplayMedia = origGetDisplay;
    };
  }, [enabled]);

  // 2. BLACKOUT triggers: screenshot keys, window blur, visibility hidden.
  //    The blackout lifts when the user clicks back on the page (or after
  //    a short timer for the keyboard-shortcut case).
  useEffect(() => {
    if (!enabled) return undefined;

    const triggerBlackout = (autoRelease = false) => {
      setBlackout(true);
      if (releaseTimer.current) clearTimeout(releaseTimer.current);
      if (autoRelease) {
        // Hold for 1.5s — long enough to ruin any screen capture in flight
        releaseTimer.current = setTimeout(() => setBlackout(false), 1500);
      }
    };
    const release = () => {
      if (releaseTimer.current) clearTimeout(releaseTimer.current);
      setBlackout(false);
    };

    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      const mac = navigator.platform.toLowerCase().includes("mac");
      // PrintScreen
      if (k === "printscreen") return triggerBlackout(true);
      // macOS: Cmd+Shift+3/4/5/6
      if (mac && e.shiftKey && e.metaKey && ["3", "4", "5", "6"].includes(k)) return triggerBlackout(true);
      // Win+Shift+S (Snipping Tool)
      if (e.shiftKey && (e.metaKey || e.getModifierState?.("OS")) && k === "s") return triggerBlackout(true);
      // Print
      if ((e.ctrlKey || e.metaKey) && k === "p") {
        e.preventDefault();
        return triggerBlackout(true);
      }
      // Save page
      if ((e.ctrlKey || e.metaKey) && k === "s") {
        e.preventDefault();
        return triggerBlackout(true);
      }
    };

    const onBlur = () => triggerBlackout(false);                 // window lost focus → likely Snipping Tool
    const onVisibility = () => {
      if (document.visibilityState === "hidden") triggerBlackout(false);
      else release();
    };
    const onFocus = () => release();
    const onClick = () => release();

    window.addEventListener("keydown", onKey);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("click", onClick);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("click", onClick);
      if (releaseTimer.current) clearTimeout(releaseTimer.current);
    };
  }, [enabled]);

  // 3. CSS to hide content on print preview
  useEffect(() => {
    if (!enabled) return undefined;
    const styleEl = document.createElement("style");
    styleEl.id = "screenshot-guard-print";
    styleEl.textContent = `
      @media print { body * { visibility: hidden !important; }
        body::before {
          content: "Printing is restricted for store-level accounts."
                   " Contact your Area Manager.";
          visibility: visible !important; display: block !important;
          font-size: 32px; padding: 60px; color: #b91c1c; font-weight: 900;
        }
      }
    `;
    document.head.appendChild(styleEl);
    return () => { styleEl.remove(); };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      <SubtleWatermark storeCode={storeCode} />
      {blackout && (
        <div
          data-testid="screenshot-blackout"
          className="fixed inset-0 z-[9999] bg-black"
          style={{ pointerEvents: "auto" }}
          onClick={() => setBlackout(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
}

/**
 * Near-invisible diagonal watermark — store code + timestamp.
 * Opacity is intentionally extremely low (~1%) so it's basically unnoticeable
 * during normal use but still leaves a forensic trail on a leaked screenshot
 * when contrast-boosted.
 */
function SubtleWatermark({ storeCode }) {
  const stamp = `${storeCode || "STORE"} · ${new Date().toISOString().slice(0, 16).replace("T", " ")}`;
  return (
    <div
      data-testid="screenshot-watermark"
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none select-none"
      style={{ zIndex: 60, mixBlendMode: "multiply" }}
    >
      <div
        className="absolute inset-0 flex flex-wrap items-start justify-start gap-x-32 gap-y-40 p-10 text-[10px] tracking-widest uppercase font-medium"
        style={{ color: "rgba(15, 23, 42, 0.012)", transform: "rotate(-22deg)" }}
      >
        {Array.from({ length: 30 }).map((_, i) => (
          <span key={i}>{stamp}</span>
        ))}
      </div>
    </div>
  );
}
